import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { AnyAction } from 'redux'
import { pathOr, split } from 'ramda'
import { ActionType } from 'typesafe-actions'
import { NAMESPACE } from './constants'
import {
  TBankingState,
  TPartiesErrorsParams,
  TPartiesErrorsResponse,
  TPartyErrorsParams,
  TPartyErrorsResponse,
} from './types'

/**
 * `http_error` as minted by `restful.catchRestError`. `restfulErrorEvent` splits the error
 * up: the response body goes on `payload`, the HTTP status on `meta`. Reading the status off
 * `payload` is what silently disabled the 422 handling below.
 */
type THttpErrorAction = {
  type: 'http_error'
  payload: {
    response: TPartyErrorsResponse & TPartiesErrorsResponse
  }
  meta: {
    status?: number
    initAction: AnyAction
  }
}

const isHttpError = (action: AnyAction): action is THttpErrorAction => action.type === 'http_error'

const initialState: TBankingState = {
  byId: {},
}

const bankingApiSlice = createSlice({
  // NB: `bankingApi` — the `http_error` case below matches on the action types this name
  // produces, while `modules/api/reducer.ts` mounts this reducer under the `banking` key.
  name: `${NAMESPACE}Api`,
  initialState,
  reducers: {
    partyErrors_request: (state, { payload }: PayloadAction<TPartyErrorsParams>) => {
      const { partyId } = payload
      if (state.byId[partyId]) {
        state.byId[partyId] = null
      }
    },
    partyErrors_success: (
      state,
      { payload }: PayloadAction<{ response: TPartyErrorsResponse | null; params: TPartyErrorsParams }>,
    ) => {
      const {
        params: { partyId },
      } = payload

      if (state.byId[partyId]) {
        state.byId[partyId] = {
          ...state.byId[partyId],
          errors: [],
        }
      }
    },
    partiesErrors_request: (state, { payload }: PayloadAction<TPartiesErrorsParams>) => {},
    partiesErrors_success: (
      state,
      { payload }: PayloadAction<{ response: TPartiesErrorsResponse | null; params: TPartiesErrorsParams }>,
    ) => {
      const partyIds = split(',', pathOr('', ['params', 'party_ids'], payload))

      partyIds.forEach(id => {
        if (state.byId[id]) {
          state.byId[id] = {
            ...state.byId[id],
            errors: [],
          }
        }
      })
    },
  },
  extraReducers: builder => {
    builder.addMatcher(isHttpError, (state, { payload, meta }) => {
      const {
        initAction: { type },
        // Off `meta`, never off `payload`: `restfulErrorEvent` moves it there. Reading it off
        // `payload` made this fall back to 500 and left both 422 branches below unreachable, so
        // no party was ever recorded as having banking errors - which in turn left every
        // consumer of those errors dead, including the check that stops an invoice being
        // approved for a beneficiary with no bank account.
        status = 500,
      } = meta

      if (type === 'bankingApi/partyErrors_request' && status === 422) {
        const {
          initAction: {
            payload: { partyId },
          },
        } = meta
        const {
          response: { errors },
        } = payload

        state.byId[partyId] = {
          errors,
        }
      }

      if (type === 'bankingApi/partiesErrors_request' && status === 422) {
        const partyIds = split(',', pathOr('', ['initAction', 'payload', 'party_ids'], meta))
        const { response } = payload

        partyIds.forEach(id => {
          if (response[id]) {
            state.byId[id] = {
              errors: response[id].errors,
            }
          }
        })
      }
    })
  },
})

const { reducer, actions: events } = bankingApiSlice

export type BankingEvent = ActionType<typeof events>

export { events, initialState, reducer }
